import { useEffect, useState } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { getContainerHistory } from "../../../connectors/containers/connectors";
import moment from "moment";
import { IContainerHistoryPoint } from "../interfaces/IContainerHistoryPoint";
import { IContainerScanEvent } from "../interfaces/IContainerScanEvent";

const SCAN_EVENT_TYPE_MAP: Record<number, IContainerScanEvent['type']> = {
    0: 'loading',
    1: 'unloading',
};

const toScanEventType = (rawType: unknown): IContainerScanEvent['type'] => {
    if (typeof rawType === 'number') {
        return SCAN_EVENT_TYPE_MAP[rawType] !== undefined ? SCAN_EVENT_TYPE_MAP[rawType] : 'loading';
    }
    return String(rawType).toLowerCase() as IContainerScanEvent['type'];
};

export const useContainerHistoryMap = (fromDate?: Date | null) => {
    const { selectedContainers } = useSelector((state: RootStateOrAny) => state.containers);
    const [loading, setIsLoading] = useState(false);
    const [containerHistoryPoints, setContainerHistoryPoints] = useState<IContainerHistoryPoint[]>([]);
    const [scanEvents, setScanEvents] = useState<IContainerScanEvent[]>([]);
    const [dateOptions, setDateOptions] = useState<any[]>([]);

    useEffect(() => {
        setContainerHistoryFromApi();
    }, []);

    const setContainerHistoryFromApi = async () => {
        setIsLoading(true);
        const response = await getContainerHistory(selectedContainers[0] ? selectedContainers[0].id : "", fromDate);
        const data = response.data.data;

        const historyPositions: IContainerHistoryPoint[] = data.historyPoints.map((x: any) => ({
            key: x.id,
            id: x.id,
            latitude: x.latitude,
            longitude: x.longitude,
            lastActiveDate: new Date(x.lastActiveDate),
            lastActiveDateStr: moment(x.lastActiveDate).format('DD.MM.YYYY HH:mm:ss'),
            firstActiveDateStr: moment(x.firstActiveDate).format('DD.MM.YYYY HH:mm:ss'),
            receptionPlaceName: x.receptionPlaceName != null ? x.receptionPlaceName : null,
            internalStoreName: x.internalStoreName != null ? x.internalStoreName : null,
        }));

        historyPositions.sort((a, b) => b.lastActiveDate.getTime() - a.lastActiveDate.getTime());

        const events: IContainerScanEvent[] = data.scanEvents.map((x: any) => ({
            date: new Date(x.date),
            type: toScanEventType(x.type),
            latitude: x.latitude,
            longitude: x.longitude,
            receptionPlaceName: x.receptionPlaceName != null ? x.receptionPlaceName : null,
            containerNumber: x.containerNumber != null ? x.containerNumber : null,
        }));

        setScanEvents(events);

        const oldestDate = new Date(Math.min(...historyPositions.map(obj => obj.lastActiveDate.getTime())));
        const today = new Date();
        const timeDiff = Math.floor((today.getTime() - oldestDate.getTime()) / (1000 * 60 * 60 * 24));
        const options: any[] = [];
        if (timeDiff > 180) options.push({ key: 180, days: 180, text: "ostatnie 180 dni" });
        if (timeDiff > 90)  options.push({ key: 90,  days: 90,  text: "ostatnie 90 dni" });
        if (timeDiff > 60)  options.push({ key: 60,  days: 60,  text: "ostatnie 60 dni" });
        if (timeDiff > 30)  options.push({ key: 30,  days: 30,  text: "ostatnie 30 dni" });
        if (timeDiff > 21)  options.push({ key: 21,  days: 21,  text: "ostatnie 21 dni" });
        if (timeDiff > 14)  options.push({ key: 14,  days: 14,  text: "ostatnie 14 dni" });
        if (timeDiff > 7)   options.push({ key: 7,   days: 7,   text: "ostatnie 7 dni" });
        if (timeDiff > 3)   options.push({ key: 3,   days: 3,   text: "ostatnie 3 dni" });
        options.push({ key: 1, days: 1, text: "ostatni 1 dzień" });
        options.push({ key: 0, days: 0, text: "wszystkie" });
        setDateOptions(options);
        setContainerHistoryPoints(historyPositions);
        setIsLoading(false);
    };

    return { containerHistoryPoints, scanEvents, loading, dateOptions };
};
